import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, CheckCircle2, BookOpen, Loader2, PlayCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/courses/${courseId}`)
      .then(({ data }) => setCourse(data))
      .catch(() => navigate('/courses'));
  }, [courseId, navigate]);

  if (!course) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" /></div>
      </div>
    );
  }

  const pct = course.chapters.length ? Math.round((course.chapters_completed / course.chapters.length) * 100) : 0;
  const nextChapter = course.chapters.find(c => !c.completed);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-detail-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate('/courses')} className="flex items-center gap-1.5 text-sm font-bold text-[#6B7280] hover:text-[#111827] mb-5" data-testid="back-to-courses-btn">
          <ArrowLeft className="w-4 h-4" /> All courses
        </button>

        <div className="quiz-card p-6 mb-6">
          <div className="flex items-start gap-4">
            <span className="text-4xl">{course.emoji}</span>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{course.title}</h1>
              <p className="text-sm text-[#6B7280] mt-1">{course.description}</p>
              <div className="flex items-center gap-3 mt-4">
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-black text-[#111827]" data-testid="course-progress-pct">{pct}%</span>
              </div>
            </div>
          </div>
          {nextChapter && (
            <button
              onClick={() => navigate(`/courses/${course.id}/learn/${nextChapter.id}`)}
              data-testid="continue-course-btn"
              className="mt-5 w-full sm:w-auto px-6 py-3 rounded-2xl font-bold text-sm bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none hover:bg-[#0284C7] flex items-center justify-center gap-2"
            >
              <PlayCircle className="w-4 h-4" />
              {course.chapters_completed > 0 ? 'Continue' : 'Start'}: {nextChapter.title}
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          {course.chapters.map((ch, idx) => {
            const quizPct = ch.quiz_total ? Math.round((ch.quiz_score / ch.quiz_total) * 100) : null;
            return (
              <button
                key={ch.id}
                onClick={() => navigate(`/courses/${course.id}/learn/${ch.id}`)}
                data-testid={`chapter-row-${ch.id}`}
                className="w-full text-left quiz-card px-5 py-4 flex items-center gap-4 hover:shadow-md transition-shadow"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-black text-sm ${
                  ch.completed ? 'bg-emerald-100 text-emerald-600' : ch.lesson_read ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  {ch.completed ? <CheckCircle2 className="w-5 h-5" /> : idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#111827] truncate">{ch.title}</p>
                  <p className="text-xs text-[#6B7280]">
                    {ch.completed ? `Completed · best quiz ${quizPct}%` : ch.lesson_read ? 'Lesson read — take the quiz' : 'Not started'}
                  </p>
                </div>
                <BookOpen className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
